import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Scroll detection — only matters on home page
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On non-home pages, always treat as scrolled (solid white bg)
  const isSolid = !isHomePage || scrolled;

  // Click outside to close mobile menu
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, [mobileOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { to: '/', label: 'Inicio' },
    { to: '/reparacion-toldos-malaga', label: 'Reparación' },
    { to: '/instalacion-toldos-malaga', label: 'Instalación' },
    { to: '/toldos-hosteleria', label: 'Hostelería' },
    { to: '/guias', label: 'Guías' },
    { to: '/contacto', label: 'Contacto' },
  ];

  // Colors when transparent (over dark hero) vs solid (white bg)
  const textClass = isSolid ? 'text-foreground-700' : 'text-white/90';
  const textHoverClass = isSolid ? 'hover:text-primary-500' : 'hover:text-white';
  const logoTextClass = isSolid ? 'text-foreground-900' : 'text-white';
  const logoSubClass = isSolid ? 'text-foreground-600' : 'text-white/65';
  const underlineClass = isSolid ? 'after:bg-primary-500' : 'after:bg-white/60';
  const hamburgerClass = isSolid
    ? 'text-foreground-700 hover:bg-background-100'
    : 'text-white hover:bg-white/10';
  const phoneClass = isSolid ? 'text-foreground-700' : 'text-white/85';

  return (
    <nav
      ref={navRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group shrink-0">
            <img
              src="/logo.svg"
              alt="Toldos Málaga - Reparación e Instalación de Toldos"
              title="Toldos Málaga"
              className="h-11 md:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative text-sm font-medium transition-all duration-300 whitespace-nowrap after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:transition-all after:duration-300 hover:after:w-full ${textClass} ${textHoverClass} ${underlineClass}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="tel:+34951120770"
              className={`hidden sm:flex items-center gap-2 text-sm font-medium transition-colors duration-300 whitespace-nowrap ${phoneClass} ${textHoverClass}`}
            >
              <i className="ri-phone-line text-lg"></i>
              <span>951 12 07 70</span>
            </a>
            <Link
              to="/presupuesto"
              className={`hidden md:inline-flex items-center gap-1.5 px-5 py-2.5 text-white text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap ${
                isSolid
                  ? 'bg-primary-500 hover:bg-primary-600'
                  : 'bg-accent-500 hover:bg-accent-400'
              }`}
            >
              <span>Presupuesto Gratis</span>
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-300 ${hamburgerClass}`}
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={mobileOpen}
            >
              <i className={`text-xl transition-transform duration-300 ${mobileOpen ? 'ri-close-line rotate-90' : 'ri-menu-line rotate-0'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      <div
        className={`lg:hidden fixed inset-x-0 top-16 md:top-20 transition-all duration-300 ease-out ${
          mobileOpen
            ? 'visible opacity-100 translate-y-0'
            : 'invisible opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <div
          className={`backdrop-blur-xl border-t ${
            isSolid
              ? 'bg-background-50/98 border-background-200/70'
              : 'bg-black/80 border-white/10'
          }`}
        >
          <div className="max-w-[1280px] mx-auto px-4 py-5 flex flex-col gap-1">
            {navLinks.map((link, i) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 ${
                  isSolid
                    ? 'text-foreground-700 hover:bg-background-100 hover:text-primary-500'
                    : 'text-white/85 hover:bg-white/10 hover:text-white'
                }`}
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                {link.label}
              </Link>
            ))}

            {/* Mobile CTA buttons */}
            <div className="flex flex-col gap-2.5 mt-4 pt-4 border-t border-white/10">
              <a
                href="tel:+34951120770"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-full text-base font-semibold transition-all duration-200 whitespace-nowrap ${
                  isSolid
                    ? 'bg-foreground-900 text-white hover:bg-primary-500'
                    : 'bg-white/15 text-white border border-white/25 hover:bg-white/25'
                }`}
              >
                <i className="ri-phone-line text-lg"></i>
                <span>Llamar 951 12 07 70</span>
              </a>
              <a
                href="https://wa.me/34627213347"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-full bg-[#25D366] text-white text-base font-semibold hover:bg-[#1fb855] transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                <span>WhatsApp</span>
              </a>
              <Link
                to="/presupuesto"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-white text-base font-semibold transition-all duration-200 whitespace-nowrap ${
                  isSolid ? 'bg-primary-500 hover:bg-primary-600' : 'bg-accent-500 hover:bg-accent-400'
                }`}
              >
                <span>Presupuesto Gratis</span>
                <i className="ri-arrow-right-line text-lg"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}